"use client";

import { useChat } from "@ai-sdk/react";
import type { AppRouter } from "@repo/api";
import { WEB_CHAT_DEBOUNCE_MS } from "@repo/common/expert-agent-chat";
import { Skeleton } from "@repo/ui/components/skeleton";
import type { inferRouterOutputs } from "@trpc/server";
import { DefaultChatTransport } from "ai";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type ComposerAttachment,
  MessageComposer,
  MessageThread,
  type ThreadMessage,
} from "@/components/chat/message-thread";
import { api } from "@/lib/trpc/react";

import { LinkPreview } from "./link-preview";
import {
  addPending,
  dropPending,
  nextExpiryAt,
  settlePending,
} from "./pending-sends";
import { ThreadHeader } from "./thread-header";
import { mergeThread } from "./thread-merge";
import { agentIsTyping } from "./typing-state";

/** A reply lands in a few seconds, so this is the beat while one is expected. */
const ACTIVE_POLL_MS = 3_000;
const IDLE_POLL_MS = 15_000;
/**
 * How long the typing indicator may claim a reply is coming.
 *
 * Generous on purpose: a turn runs the model several times and can legitimately
 * take half a minute, so this is the point past which silence is likelier than
 * slowness, not a latency target.
 */
const REPLY_WAIT_CEILING_MS = 90_000;

const OPPORTUNITY_PAGE = /^\/me\/opportunities\/([^/]+)$/;

/**
 * The study an opportunity page is open on, or undefined anywhere else.
 *
 * Read from the route rather than `useParams`, which also hands back a
 * `studyId` on the partner referral page, where it is a study they refer
 * people to rather than one they are on. Whether it is theirs is the server's
 * check; this only says which one is on screen.
 */
function studyIdOnScreen(pathname: string): string | undefined {
  const studyId = OPPORTUNITY_PAGE.exec(pathname)?.[1];
  return studyId === "history" ? undefined : studyId;
}

/** One row of the stored conversation, as the router hands it over. */
type ConversationRow =
  inferRouterOutputs<AppRouter>["expertAgentChat"]["list"][number];

/**
 * One stored row as one bubble.
 *
 * Shared by the live read and the paged one deliberately: they were two copies
 * of the same map, and the paged copy silently dropped `images`, so scrolling
 * back turned a screenshot into an empty bubble. One function is what stops the
 * two from drifting again.
 */
export function asThreadMessage(row: ConversationRow): ThreadMessage {
  return {
    id: row.id,
    role: row.role,
    body: row.body,
    channel: "WEB",
    createdAt: row.createdAt,
    images: row.images,
    clientId: row.clientId,
  };
}

/**
 * The expert's own thread with Sophia.
 *
 * Two sources, deliberately. The stored conversation comes from our table and
 * is the record; `useChat` owns only the turn in flight, because the agent
 * answers in several bubbles and each is written as it is sent. So the poll is
 * what makes the later bubbles of a turn appear, not the streaming response.
 */
export function SophiaConversation({
  expanded,
  onToggleExpand,
  onClose,
  initialDraft = "",
  queued = null,
  onQueuedTaken,
  freshStart = false,
  showHeader = true,
}: {
  expanded?: boolean;
  onToggleExpand?: () => void;
  onClose?: () => void;
  /** A message written before this mounted, put back in the box unsent. */
  initialDraft?: string;
  /**
   * What an opener elsewhere on the page asked to say: a message on its own is
   * sent, and files land in the box with the message beside them. Each id is
   * taken once, and `onQueuedTaken` says so.
   */
  queued?: { id: number; message?: string; files?: File[] } | null;
  onQueuedTaken?: () => void;
  /**
   * Draw only what is said after this mounted. The thread behind it is still
   * one thread, so Sophia keeps the context; the reader just is not dropped
   * into the middle of it.
   */
  freshStart?: boolean;
  /** False where the page around it already says whose thread this is. */
  showHeader?: boolean;
} = {}) {
  const [draft, setDraft] = useState(initialDraft);
  /**
   * Read here rather than passed in, and read at send rather than at mount.
   *
   * The panel outlives a client-side navigation -- the layout it hangs off
   * does not remount -- so a route captured when it opened would report the
   * page they asked from three pages ago for the rest of the session. Named
   * server-side: this is the side that has to distrust the value anyway, so
   * translating it here would only mean keeping the same table twice.
   */
  const pathname = usePathname();
  /**
   * When the reader last sent something, or null when nothing is outstanding.
   *
   * A timestamp rather than a boolean because clearing it needs to know which
   * replies are new: the thread almost always ends in one of Sophia's, and a
   * boolean cannot tell that one from the answer being waited on.
   */
  const [awaitingSince, setAwaitingSince] = useState<number | null>(null);
  const awaitingReply = awaitingSince !== null;

  // The indicator lags the send by the server's debounce window, so it never
  // claims Sophia is typing while the turn is still gathering messages. A
  // re-render is scheduled for the moment it becomes true, since nothing else
  // would wake the component at that instant.
  const [typingVisible, setTypingVisible] = useState(false);
  useEffect(() => {
    if (awaitingSince === null) {
      setTypingVisible(false);
      return;
    }
    setTypingVisible(agentIsTyping(awaitingSince, Date.now()));
    const timer = setTimeout(
      () => {
        setTypingVisible(true);
      },
      Math.max(0, awaitingSince + WEB_CHAT_DEBOUNCE_MS - Date.now()),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [awaitingSince]);
  // Drawn from module state, not component state: the panel unmounts when it
  // closes, and a message sent inside the turn's debounce window has no row to
  // come back to yet.
  const [sent, setSent] = useState<ThreadMessage[]>([]);
  // Every row the thread has seen, not two windows concatenated. The older page
  // is fetched once and frozen while the newest page slides forward, so joining
  // them end to end drops whatever fell between the two.
  const [seen, setSeen] = useState<ReadonlyMap<string, ThreadMessage>>(
    () => new Map(),
  );
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);

  const stored = api.expertAgentChat.list.useQuery(undefined, {
    refetchInterval: awaitingReply ? ACTIVE_POLL_MS : IDLE_POLL_MS,
    // A thread nobody is looking at costs no queries.
    refetchIntervalInBackground: false,
  });

  const transport = useMemo(
    () => new DefaultChatTransport({ api: "/api/chat" }),
    [],
  );
  // The send in flight, and the ids the server already holds, both as refs:
  // `onError` is created once and would otherwise close over the first render.
  //
  // It is the only signal that a send failed. `sendMessage` resolves either
  // way, so the promise says nothing about delivery -- a `.catch` on it never
  // runs, which is why an undelivered message used to sit in the thread drawn
  // exactly like a delivered one.
  const inFlightRef = useRef<string | null>(null);
  const storedIdsRef = useRef<readonly string[]>([]);

  const { sendMessage } = useChat({
    transport,
    onError: () => {
      const failed = inFlightRef.current;
      inFlightRef.current = null;
      setAwaitingSince(null);
      if (!failed) {
        return;
      }
      // What they typed goes back in the box rather than being thrown away
      // with the bubble.
      const taken = dropPending(failed);
      setSent(settlePending(storedIdsRef.current, Date.now()));
      if (taken) {
        setDraft((current) => current || taken.body);
      }
    },
  });

  const utils = api.useUtils();
  // The launcher's dot reads `unread`, and a poll that was already out when
  // the panel opened can bring the old answer back, so the cache is set again
  // once the server has the rows as read.
  const { mutate: markRead } = api.expertAgentChat.markRead.useMutation({
    onSuccess: () => {
      utils.expertAgentChat.unread.setData(undefined, (current) =>
        current ? { ...current, unread: false } : current,
      );
    },
  });

  const recent: ThreadMessage[] = useMemo(
    () => (stored.data ?? []).map(asThreadMessage),
    [stored.data],
  );

  useEffect(() => {
    if (recent.length > 0) {
      setSeen((held) => mergeThread(held, recent));
    }
  }, [recent]);

  const history = useMemo(() => [...seen.values()], [seen]);

  // Taken from the first read rather than from `seen`, which fills a render
  // later: a snapshot of that would be empty and let the whole history in.
  const [presentAtOpen, setPresentAtOpen] =
    useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (freshStart && presentAtOpen === null && stored.data) {
      setPresentAtOpen(new Set(stored.data.map((row) => row.id)));
    }
  }, [freshStart, presentAtOpen, stored.data]);
  const shown = !freshStart
    ? history
    : presentAtOpen
      ? history.filter((message) => !presentAtOpen.has(message.id))
      : [];

  // Marked up to the newest reply drawn, not to now, so one that lands after
  // this read keeps its dot; a fresh start draws nothing from before it
  // mounted, so it marks nothing from then either. A string, so a poll that
  // returns the same row does not fire again.
  const drawnRows = !freshStart
    ? stored.data
    : presentAtOpen
      ? stored.data?.filter((row) => !presentAtOpen.has(row.id))
      : undefined;
  const newest = drawnRows?.at(-1);
  const newestReplyAt =
    newest && (newest.role === "AGENT" || newest.role === "OPERATOR")
      ? new Date(newest.createdAt).toISOString()
      : null;
  useEffect(() => {
    if (newestReplyAt) {
      markRead({ upTo: new Date(newestReplyAt) });
    }
  }, [newestReplyAt, markRead]);

  // A sent message stops being ours to draw the moment the server returns it.
  // When each of the reader's stored messages was written, not how many are on
  // screen: the page holds a fixed number of the newest rows, so past that a
  // count taken over it stops climbing and a sent bubble never settles.
  // The ids the server has stored, which is how a drawn bubble knows its own
  // row has arrived without anyone inferring which row belongs to which send.
  const storedFromReader = useMemo(
    () =>
      history
        .map((message) => message.clientId)
        .filter((id): id is string => id !== null),
    [history],
  );

  // Settles on every read, then comes back on its own for the ceiling.
  //
  // The ceiling used to be applied only when a stored row arrived, and a turn
  // that dies inside the webhook produces none -- so a bubble for a message
  // that never went stayed for the life of the tab. The timer re-arms because a
  // send made after it was set expires later than the one it was scheduled for.
  useEffect(() => {
    storedIdsRef.current = storedFromReader;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const settle = () => {
      setSent(settlePending(storedFromReader, Date.now()));
      const expiresAt = nextExpiryAt();
      if (expiresAt !== null) {
        timer = setTimeout(settle, Math.max(0, expiresAt - Date.now()) + 100);
      }
    };
    settle();
    return () => {
      clearTimeout(timer);
    };
  }, [storedFromReader]);

  // The panel mounts on open, so this fires once per open: a support panel
  // somebody has just asked for should be ready to type into, and without it
  // focus stays on the launcher and the first Tab leaves the panel entirely.
  useEffect(() => {
    if (!stored.isPending) {
      // Without scrolling: on the help page the thread sits below the fold,
      // and focusing it would carry the reader past the page they opened.
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [stored.isPending]);

  // The turn is over once the agent's own bubble is in the record.
  // A reply that is newer than the send, not merely the newest message in the
  // thread. After the first turn the last stored message is always Sophia's
  // previous answer, so the plain check cleared the wait on the same commit
  // that set it: no typing indicator, and the poll never went to its active
  // rate.
  useEffect(() => {
    if (awaitingSince === null) {
      return;
    }
    const answered = recent.some(
      (message) =>
        message.role === "AGENT" &&
        new Date(message.createdAt).getTime() > awaitingSince,
    );
    if (answered) {
      setAwaitingSince(null);
    }
  }, [recent, awaitingSince]);

  // The send can be accepted and the turn still die afterwards -- the webhook is
  // acked before the agent runs, so a throw in there answers nothing and rejects
  // nothing. Without this the indicator says "typing" until the tab is closed,
  // which reads as a reply that is still coming rather than one that is not.
  useEffect(() => {
    if (awaitingSince === null) {
      return;
    }
    const timer = setTimeout(() => {
      setAwaitingSince(null);
    }, REPLY_WAIT_CEILING_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [awaitingSince]);

  const loadOlder = useCallback(
    async (el: HTMLDivElement) => {
      const oldest = history[0];
      if (!oldest || loadingOlder || exhausted) {
        return;
      }
      setLoadingOlder(true);
      const anchor = el.scrollHeight - el.scrollTop;
      try {
        const page = await utils.expertAgentChat.list.fetch({
          // Both halves of the sort key: a row tied on the millisecond would
          // otherwise be older than nothing and newer than nothing, and land
          // on neither page.
          before: { createdAt: new Date(oldest.createdAt), id: oldest.id },
        });
        if (page.length === 0) {
          setExhausted(true);
          return;
        }
        setSeen((held) => mergeThread(held, page.map(asThreadMessage)));
        // Hold the reader's place: without this, prepending yanks them upward.
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight - anchor;
        });
      } finally {
        setLoadingOlder(false);
      }
    },
    [history, loadingOlder, exhausted, utils],
  );

  /**
   * Upload first, send second. The message names urls the server has to be able
   * to fetch, so a reference that does not resolve yet is worse than a wait --
   * the composer keeps send disabled until every one of these lands.
   */
  const attachFiles = (files: File[]) => {
    for (const file of files) {
      const id = `${String(Date.now())}-${file.name}`;
      const previewUrl = URL.createObjectURL(file);
      setAttachments((current) => [
        ...current,
        { id, name: file.name, previewUrl },
      ]);
      const form = new FormData();
      form.append("file", file);
      void fetch("/api/expert-agent/attachment", {
        method: "POST",
        body: form,
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(String(response.status));
          }
          return response.json() as Promise<{ url: string; mimeType: string }>;
        })
        .then((uploaded) => {
          setAttachments((current) =>
            current.map((attachment) =>
              attachment.id === id
                ? {
                    ...attachment,
                    url: uploaded.url,
                    mimeType: uploaded.mimeType,
                  }
                : attachment,
            ),
          );
        })
        .catch(() => {
          setAttachments((current) =>
            current.map((attachment) =>
              attachment.id === id
                ? { ...attachment, failed: true }
                : attachment,
            ),
          );
        });
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments((current) => {
      const going = current.find((attachment) => attachment.id === id);
      if (going) {
        URL.revokeObjectURL(going.previewUrl);
      }
      return current.filter((attachment) => attachment.id !== id);
    });
  };

  const sendBody = (
    body: string,
    images: { url: string; mimeType: string }[],
  ) => {
    // A picture on its own is a message.
    if (!body && images.length === 0) {
      return;
    }
    setAwaitingSince(Date.now());
    // Drawn immediately. Waiting for the round trip is what made sending feel
    // laggy even though the send itself is fast.
    // Minted here so the message can be recognised when it comes back: it
    // travels in the message's metadata, the turn files it on the row, and the
    // read hands it straight back.
    //
    // Not as the SDK's own `messageId`: passing one of those to `sendMessage`
    // stops the request going out at all, silently, which is a thing only a
    // browser can tell you. Metadata is the path the attachments already use
    // and the one that is known to arrive.
    const clientId = crypto.randomUUID();
    addPending({ id: clientId, body, images, at: Date.now() });
    setSent(settlePending(storedFromReader, Date.now()));
    // A rejected send is a turn that will never answer, so the indicator has to
    // stop on its own: it is the only thing telling them a reply is coming.
    inFlightRef.current = clientId;
    void sendMessage({
      text: body,
      metadata: {
        images,
        clientId,
        pathname,
        studyId: studyIdOnScreen(pathname),
      },
    })
      .catch(() => {
        setAwaitingSince(null);
      })
      .finally(() => {
        void stored.refetch();
      });
    inputRef.current?.focus();
  };

  // Only the composer's own send empties the composer: a message queued from
  // elsewhere on the page leaves what the reader was writing where it is.
  const send = () => {
    const body = draft.trim();
    const images = attachments
      .filter((attachment) => attachment.url)
      .map((attachment) => ({
        url: attachment.url as string,
        mimeType: attachment.mimeType as string,
      }));
    if (!body && images.length === 0) {
      return;
    }
    setDraft("");
    setAttachments([]);
    sendBody(body, images);
  };

  // Taken once the thread has loaded, so the message lands under the history it
  // follows rather than ahead of it. Called through a ref because the handlers
  // it uses are rebuilt every render; the id is what stops a second send.
  const takenQueueId = useRef<number | null>(null);
  const takeQueued = useRef(() => {});
  takeQueued.current = () => {
    if (!queued || stored.isPending || takenQueueId.current === queued.id) {
      return;
    }
    takenQueueId.current = queued.id;
    if (queued.files?.length) {
      attachFiles(queued.files);
      const message = queued.message;
      if (message) {
        setDraft((current) =>
          current.trim() ? `${current.trimEnd()}\n${message}` : message,
        );
      }
    } else if (queued.message) {
      sendBody(queued.message.trim(), []);
    }
    onQueuedTaken?.();
  };
  useEffect(() => {
    takeQueued.current();
  }, [queued, stored.isPending]);

  const header = showHeader ? (
    <ThreadHeader
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      onClose={onClose}
    />
  ) : null;

  if (stored.isPending) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col">
        {header}
        <div className="flex flex-1 flex-col gap-4 px-4 py-4">
          <Skeleton className="h-16 w-2/3 rounded-[20px]" />
          <Skeleton className="ml-auto h-12 w-1/2 rounded-[20px]" />
          <Skeleton className="h-20 w-3/5 rounded-[20px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {header}
      <MessageThread
        messages={[...shown, ...sent]}
        agentTyping={typingVisible}
        onReachTop={freshStart ? undefined : loadOlder}
        attribution="Sophia"
        renderLinkPreview={(url, ownSide) => (
          <LinkPreview key={url} url={url} ownSide={ownSide} />
        )}
        emptyState={
          <p className="text-muted-foreground max-w-sm text-center text-sm">
            Nothing here yet. Ask Sophia anything: an application you&apos;re
            waiting on, a payment, or how something works.
          </p>
        }
      />
      <MessageComposer
        value={draft}
        onChange={setDraft}
        onSend={send}
        // Never blocked while she answers: the turn debounces server-side, so
        // a second line joins the same turn rather than racing it.
        disabled={false}
        placeholder="Message Sophia"
        inputRef={inputRef}
        attachments={attachments}
        onAttachFiles={attachFiles}
        onRemoveAttachment={removeAttachment}
      />
    </div>
  );
}
